import React, { useState, useRef, useCallback, useEffect, useContext } from 'react';
import {
  HeaderTop,
  HeaderLeft,
  BellIcon,
  Title,
  ThreeDotsButton,
  DropdownMenu,
  DropdownMenuItem,
  UnreadToggleContainer,
  UnreadLabel,
  HeaderRight
} from '../styles';
import { createPortal } from 'react-dom';
import { DynamicSVG, Images, Ellipsis } from '@lua-frontend/numu-ui';
import { ToggleSwitch } from '../../toggle-button';
import { NotificationPanelContext } from '../index';

const HeaderTitlebar = ({ onMarkAllAsRead }) => {
  const { filterOptions, dispatch, setFilterOptions } = useContext(NotificationPanelContext);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [dropdownCoords, setDropdownCoords] = useState(null);
  const threeDotsRef = useRef(null);
  const dropdownRef = useRef(null);

  // Function to calculate and update dropdown position
  const updateDropdownPosition = useCallback(() => {
    if (isDropdownOpen && threeDotsRef.current) {
      const rect = threeDotsRef.current.getBoundingClientRect();
      setDropdownCoords({
        top: rect.bottom + 10,
        left: rect.right - 90 // Adjust based on dropdown width
      });
    }
  }, [isDropdownOpen]);

  // Update position when dropdown opens
  useEffect(() => {
    if (isDropdownOpen) {
      updateDropdownPosition();
    }
  }, [isDropdownOpen, updateDropdownPosition]);

  // Handle window resize and scroll events
  useEffect(() => {
    if (!isDropdownOpen) return;

    let rafId = null;

    const handleResize = () => {
      // Use requestAnimationFrame to debounce resize events
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      rafId = requestAnimationFrame(() => {
        updateDropdownPosition();
      });
    };

    const handleScroll = () => {
      // Update position on scroll as well
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      rafId = requestAnimationFrame(() => {
        updateDropdownPosition();
      });
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll, true); // Use capture phase to catch all scrolls

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll, true);
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [isDropdownOpen, updateDropdownPosition]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        threeDotsRef.current &&
        !threeDotsRef.current.contains(e.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target)
      ) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isDropdownOpen]);

  const handleMarkAllAsRead = () => {
    if (onMarkAllAsRead) {
      onMarkAllAsRead();
    }
    setIsDropdownOpen(false);
  };

  return (
    <HeaderTop>
      <HeaderLeft>
        <BellIcon>
          <DynamicSVG
            svgPath={Images.bellIconSvg}
            style={{ color: '#0077CC', width: 20, height: 20 }}
          />
        </BellIcon>
        <Title>Notifications</Title>
        <div style={{ position: 'relative' }}>
          <ThreeDotsButton
            ref={threeDotsRef}
            onClick={(e) => {
              e.stopPropagation();
              setIsDropdownOpen(!isDropdownOpen);
            }}
            aria-label="More options"
          >
            <Ellipsis style={{ background: '#797E86' }} />
            <Ellipsis style={{ background: '#797E86', marginLeft: '1px' }} />
            <Ellipsis style={{ background: '#797E86', marginLeft: '1px' }} />
          </ThreeDotsButton>

          {isDropdownOpen &&
            dropdownCoords &&
            createPortal(
              <DropdownMenu ref={dropdownRef} top={dropdownCoords.top} left={dropdownCoords.left}>
                <DropdownMenuItem onClick={handleMarkAllAsRead}>
                  <DynamicSVG
                    svgPath={Images.markAllAsReadSvg}
                    style={{ color: '#0095FF', width: 20, height: 20, marginTop: '3px' }}
                  />
                  Mark all as read
                </DropdownMenuItem>
              </DropdownMenu>,
              document.body
            )}
        </div>
      </HeaderLeft>
      <HeaderRight>
        <UnreadToggleContainer>
          <UnreadLabel>Unread</UnreadLabel>
          <ToggleSwitch
            status={filterOptions.isUnRead}
            onClick={() => dispatch(setFilterOptions({ isUnRead: !filterOptions.isUnRead }))}
          />
        </UnreadToggleContainer>
      </HeaderRight>
    </HeaderTop>
  );
};

export default HeaderTitlebar;
